import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import { useAddShowcaseSite, useRemoveShowcaseSite, useSendShowcaseGuide, useShowcase } from "@/hooks/useShowcase";
import Step from "./ShowcaseSetupStep";
import { GUIDES, MAX_SITES, SNIPPETS, copyText, dateOf, hostOf, lastSeenOf } from "./showcaseSetupModel";
import "./ShowcaseSetup.scss";

export { GUIDES, MAX_SITES, hostOf, lastSeenOf };

const ShowcaseSetup = ({ brokerageId, onGo }) => {
  const query = useShowcase(brokerageId);
  const addSite = useAddShowcaseSite(brokerageId);
  const removeSite = useRemoveShowcaseSite(brokerageId);
  const sendGuide = useSendShowcaseGuide(brokerageId);
  const [host, setHost] = useState("");
  const [hostError, setHostError] = useState("");
  const [snippet, setSnippet] = useState("script");
  const [guide, setGuide] = useState("wordpress");
  const [email, setEmail] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [copied, setCopied] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const hostRef = useRef(null);
  const snippetRef = useRef(null);

  const showcase = query.data?.showcase;
  if (!showcase) {
    return query.isError ? (
      <div className="showcase-setup__state">
        Your setup did not load.{" "}
        <button type="button" className="btn btn-link p-0 align-baseline" onClick={() => query.refetch()}>
          Try again
        </button>
      </div>
    ) : (
      <div className="showcase-setup__state" aria-busy="true">
        Loading your setup…
      </div>
    );
  }

  const checklist = showcase.checklist || {};
  const sites = showcase.sites || [];
  const seen = lastSeenOf(sites);
  const firstOpen = ["subscribed", "siteAdded", "installed", "live"].find((step) => !checklist[step]);
  const allDone = !firstOpen;
  const codeOpen = showCode || !allDone;
  const code = snippet === "script" ? showcase.embedScript : snippet === "iframe" ? showcase.iframeCode : showcase.hostedUrl;
  const activeGuide = GUIDES.find((item) => item.key === guide) || GUIDES[0];

  const submitHost = (event) => {
    event.preventDefault();
    const clean = hostOf(host);
    if (!clean || !clean.includes(".")) {
      setHostError("Enter a website address, like yourbrokerage.com.");
      return;
    }
    if (sites.some((site) => site.host === clean)) {
      setHostError(`${clean} is already on your list.`);
      return;
    }
    addSite.mutate(clean, {
      onSuccess: () => {
        setHost("");
        setHostError("");
      },
      onError: (error) => setHostError(error.fields?.host ? "Enter a website address, like yourbrokerage.com." : error.message),
    });
  };

  const copy = async () => {
    if (!code) return;
    const ok = await copyText(code);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else toast.error("Copy did not work. Select the code and copy it by hand.");
  };

  const submitGuide = (event) => {
    event.preventDefault();
    const to = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) {
      toast.error("Enter your web person's email address.");
      return;
    }
    sendGuide.mutate(to, {
      onSuccess: () => {
        setSentTo(to);
        setEmail("");
      },
      onError: (error) => toast.error(error.message),
    });
  };

  const actionFor = (step, label, run) =>
    firstOpen === step ? (
      <button type="button" className="btn btn-primary showcase-setup__button" onClick={run}>
        {label}
      </button>
    ) : null;

  return (
    <section className="content-block showcase-setup">
      <div className="title-sec">
        <h4>{allDone ? "Your websites" : "Put the showcase on your\u00a0website"}</h4>
        {allDone ? (
          <a role="button" tabIndex={0} aria-expanded={codeOpen} onClick={() => setShowCode(!showCode)}>
            {codeOpen ? "Hide the code" : "Get the code"}
          </a>
        ) : null}
      </div>
      <p className="showcase-setup__hint">
        {allDone
          ? "Live on your website. It loads only on these addresses, so add each version people use, like www and the bare\u00a0domain."
          : "Four steps, about 10 minutes. Each one turns green on its own as you finish\u00a0it."}
      </p>

      {allDone ? null : (
        <ol className="showcase-setup__steps">
          <Step
            number={1}
            done={checklist.subscribed}
            title="Subscribe for $59 a month"
            detail={showcase.comped ? "Included by Gitcha for your office." : "Card checkout through Stripe. Cancel any time from Manage billing."}
            action={actionFor("subscribed", "Subscribe", () => onGo("billing"))}
          />
          <Step
            number={2}
            done={checklist.siteAdded}
            title="Add your website address"
            detail={sites.length ? sites.map((site) => site.host).join(", ") : `The showcase only loads on sites you list. Up to ${MAX_SITES}.`}
            action={actionFor("siteAdded", "Add website", () => hostRef.current?.focus())}
          />
          <Step
            number={3}
            done={checklist.installed}
            title="Put it on your site"
            detail="Paste one line, or email the steps to whoever edits your website."
            action={actionFor("installed", "Get the code", () => snippetRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }))}
          />
          <Step
            number={4}
            done={checklist.live}
            title="We see it live"
            detail={
              seen
                ? `Last seen on ${seen.host}, ${dateOf(seen.lastSeenAt)}.`
                : "Confirmed the first time a visitor loads it on one of your sites."
            }
          />
        </ol>
      )}

      <div className="showcase-setup__part">
        {allDone ? null : <h5 className="showcase-setup__subhead">Your websites</h5>}
        {allDone ? null : (
          <p className="showcase-setup__hint">
            The showcase loads only on these addresses. Add each version people use, like www and the bare&nbsp;domain.
          </p>
        )}
        {sites.length ? (
          <ul className="showcase-setup__sites">
            {sites.map((site) => (
              <li key={site.id}>
                <span className="showcase-setup__host">{site.host}</span>
                <span className="showcase-setup__seen">
                  {site.lastSeenAt ? `Seen ${dateOf(site.lastSeenAt)}` : "Not seen yet"}
                </span>
                <button
                  type="button"
                  className="showcase-setup__quiet"
                  disabled={removeSite.isPending}
                  aria-label={`Remove ${site.host}`}
                  onClick={() => removeSite.mutate(site.id, { onError: (error) => toast.error(error.message) })}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {sites.length < MAX_SITES ? (
          <form className="showcase-setup__row" onSubmit={submitHost} noValidate>
            <input
              ref={hostRef}
              value={host}
              onChange={(event) => {
                setHost(event.target.value);
                setHostError("");
              }}
              placeholder="yourbrokerage.com"
              inputMode="url"
              aria-label="Website address"
              aria-invalid={Boolean(hostError)}
            />
            <button type="submit" className="showcase-setup__link" disabled={!host.trim() || addSite.isPending}>
              {addSite.isPending ? "Adding…" : "Add"}
            </button>
          </form>
        ) : (
          <p className="showcase-setup__hint">You have {MAX_SITES} sites, the most a showcase can use. Remove one to add&nbsp;another.</p>
        )}
        {hostError ? (
          <p className="showcase-setup__error" role="alert">
            {hostError}
          </p>
        ) : null}
      </div>

      {codeOpen ? (
        <>
          <div className="showcase-setup__part" ref={snippetRef}>
            <h5 className="showcase-setup__subhead">Your code</h5>
            <p className="showcase-setup__hint">
              {checklist.subscribed
                ? "Paste it where the buyers should appear."
                : "You can install it now. It stays blank on your site until you subscribe."}
            </p>
            <div className="showcase-setup__platforms" role="radiogroup" aria-label="Code type">
              {SNIPPETS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role="radio"
                  aria-checked={snippet === item.key}
                  className={snippet === item.key ? "is-on" : ""}
                  onClick={() => setSnippet(item.key)}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <div className="showcase-setup__code">
              <pre>
                <code>{code || "Your code is being prepared."}</code>
              </pre>
              <button type="button" className="showcase-setup__copy" onClick={copy} disabled={!code}>
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <p className="showcase-setup__hint showcase-setup__hint--after">
              {snippet === "script"
                ? "Best choice. The frame grows to fit its cards."
                : snippet === "iframe"
                  ? "For sites that do not allow scripts. It has a fixed height, so set it to fit your page."
                  : "No website edits at all. Link to this page from your menu or emails."}
            </p>

            <h4 className="showcase-setup__subhead">Steps for your website&nbsp;builder</h4>
            <div className="showcase-setup__platforms" role="radiogroup" aria-label="Website builder">
              {GUIDES.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role="radio"
                  aria-checked={guide === item.key}
                  className={guide === item.key ? "is-on" : ""}
                  onClick={() => setGuide(item.key)}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <ol className="showcase-setup__guide">
              {activeGuide.steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
            {activeGuide.note ? <p className="showcase-setup__hint">{activeGuide.note}</p> : null}
          </div>

          <div className="showcase-setup__part">
            <h5 className="showcase-setup__subhead">Email the steps to your web&nbsp;person</h5>
            <p className="showcase-setup__hint">They get the code and the steps for every website builder. Nothing else is&nbsp;shared.</p>
            <form className="showcase-setup__row" onSubmit={submitGuide} noValidate>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="webperson@yourbrokerage.com"
                autoComplete="off"
                aria-label="Web person's email"
              />
              <button type="submit" className="showcase-setup__link" disabled={!email.trim() || sendGuide.isPending}>
                {sendGuide.isPending ? "Sending…" : "Send the steps"}
              </button>
            </form>
            {sentTo ? (
              <p className="showcase-setup__sent" role="status">
                Sent to {sentTo}. Step 4 turns green once they publish&nbsp;it.
              </p>
            ) : null}
          </div>
        </>
      ) : null}
    </section>
  );
};

export default ShowcaseSetup;
